import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import {
  getReceivables,
  getClubTeams,
  getClubCoaches,
  type ReceivableRow,
  type ReceivablesResponse,
} from "../../services/api";
import { formatDate } from "../../lib/dateFormat";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { AdminCard } from "../../components/admin/AdminCard";
import DateInput from "../../components/DateInput";
import ClubOnlinePayments from "../../components/club/ClubOnlinePayments";
import ClubWaivers from "../../components/club/ClubWaivers";
import ClubCoachCash from "../../components/club/ClubCoachCash";
import ChargePlanDialog from "../../components/club/ChargePlanDialog";
import { useFeatures } from "../../hooks/useFeatures";

type Team = { id: string; name: string };
// getClubCoaches returns the coach_profiles row with the user nested; the
// filter needs the USER id, since teams.coach_id references users.id.
type Coach = { id: string; user: { id: string; full_name: string | null; email: string } };

/** First / last day of the current month as yyyy-mm-dd. */
function monthBounds() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const first = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const last = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(lastDay)}`;
  return { first, last };
}

export default function ClubReceivables() {
  const { t } = useTranslation();
  const { activeClubId } = useAuth();
  const { features } = useFeatures();

  const [data, setData] = useState<ReceivablesResponse | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { first, last } = useMemo(monthBounds, []);
  const [from, setFrom] = useState(first);
  const [to, setTo] = useState(last);
  const [teamId, setTeamId] = useState("");
  const [coachId, setCoachId] = useState("");
  const [status, setStatus] = useState("");

  // Facets of one question: what is still owed, what has already come in, what
  // the club gave away, and where the cash physically is. All on one page
  // because an admin chasing a restanță is the same person who then has to
  // take the cash off the coach who collected it and refund a double payment.
  type Tab = "due" | "coach-cash" | "received" | "waivers";
  const [params, setParams] = useSearchParams();
  const urlTab = params.get("tab");
  const [tab, setTab] = useState<Tab>(
    urlTab === "coach-cash" || urlTab === "received" || urlTab === "waivers" ? urlTab : "due"
  );

  /** Switching a tab records it in the URL, so the page can be linked to and
   *  the back button means something. */
  const goTab = (next: Tab) => {
    setTab(next);
    const copy = new URLSearchParams(params);
    if (next === "due") copy.delete("tab");
    else copy.set("tab", next);
    setParams(copy, { replace: true });
  };

  // The period a plan is being set on, and whose name to show on the dialog.
  const [planFor, setPlanFor] = useState<{ chargeId: string; athleteName: string } | null>(null);

  const money = (cents: number, currency = "RON") =>
    new Intl.NumberFormat("ro-RO", { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(
      cents / 100
    );

  const recurrenceLabels: Record<string, string> = {
    hourly: t("products.recurrence_hourly", "pe oră"),
    weekly: t("products.recurrence_weekly", "săptămânal"),
    monthly: t("products.recurrence_monthly", "lunar"),
  };

  const load = useCallback(async () => {
    if (!activeClubId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await getReceivables(activeClubId, {
        from: from || undefined,
        to: to || undefined,
        team_id: teamId || undefined,
        coach_id: coachId || undefined,
        status: status || undefined,
      });
      setData(res);
    } catch (e) {
      console.error("Failed to load receivables:", e);
      setError(true);
    }
    setLoading(false);
  }, [activeClubId, from, to, teamId, coachId, status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!activeClubId) return;
    getClubTeams(activeClubId).then(setTeams).catch(() => setTeams([]));
    getClubCoaches(activeClubId).then(setCoaches).catch(() => setCoaches([]));
  }, [activeClubId]);

  const clearFilters = () => {
    setFrom("");
    setTo("");
    setTeamId("");
    setCoachId("");
    setStatus("");
  };
  const hasFilters = !!(from || to || teamId || coachId || status);

  const statusChip = (row: ReceivableRow) => {
    // A waived period with nothing collected is not "Încasat". The club gave
    // this away, and the screen has to say so.
    if (row.waived_cents > 0 && row.collected_cents === 0)
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-bold rounded-full bg-sky-100 text-sky-700">
          {t("receivables.waived", "Scutit")}
        </span>
      );
    if (row.payment_status === "paid")
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-bold rounded-full bg-emerald-100 text-emerald-700">
          {t("receivables.paid", "Încasat")}
        </span>
      );
    if (row.is_overdue)
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-bold rounded-full bg-rose-100 text-rose-700">
          {t("receivables.overdue", "Restanță")}
        </span>
      );
    return (
      <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-bold rounded-full bg-amber-100 text-amber-700">
        {t("receivables.pending", "De încasat")}
      </span>
    );
  };

  /** Where the money is: with a coach until an admin records the handover. */
  const cashLocation = (row: ReceivableRow) => {
    const lp = row.last_payment;
    if (!lp) return <span className="text-slate-300">—</span>;
    if (lp.payment_method !== "cash")
      return (
        <span className="text-slate-500">
          {lp.payment_method === "card" ? t("receivables.card", "Card") : t("receivables.bank", "Transfer bancar")}
        </span>
      );
    if (!lp.collected_by_coach)
      return <span className="text-slate-500">{t("receivables.cashClub", "Numerar la club")}</span>;
    return (
      <span className={lp.handed_over ? "text-slate-500" : "text-amber-700 font-medium"}>
        {lp.collected_by_coach.full_name || t("receivables.coach", "Antrenor")}
        <span className="block text-[11px] text-slate-400">
          {lp.handed_over ? t("receivables.handedOver", "predat clubului") : t("receivables.withCoach", "banii sunt la antrenor")}
        </span>
      </span>
    );
  };

  const selectCls =
    "px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-0";

  const tiles: { label: string; value: string; tone: string; goTo?: Tab }[] = data
    ? [
        { label: t("receivables.outstanding", "De încasat"), value: money(data.totals.outstanding_cents), tone: "text-amber-600" },
        { label: t("receivables.overdueTotal", "Restanțe"), value: money(data.totals.overdue_cents), tone: "text-rose-600" },
        { label: t("receivables.collected", "Încasat"), value: money(data.totals.collected_cents), tone: "text-emerald-600" },
        {
          label: t("receivables.cashWithCoaches", "Numerar la antrenori"),
          value: money(data.totals.cash_with_coaches_cents),
          tone: "text-indigo-600",
          // This figure is the one somebody wants to act on rather than read,
          // so the tile is the way into the panel that lets them.
          goTo: "coach-cash",
        },
        ...(data.totals.waived_cents > 0
          ? [
              {
                label: t("receivables.waivedTotal", "Scutit"),
                value: money(data.totals.waived_cents),
                tone: "text-sky-600",
              },
            ]
          : []),
      ]
    : [];

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
        <AdminPageHeader
          title={t("receivables.title", "De încasat")}
          subtitle={t(
            "receivables.subtitle",
            "Ce are clubul de încasat de la sportivi, și unde sunt banii deja strânși."
          )}
          icon={<ClubNavIcon id="receivables" className="w-6 h-6" />}
        />

        {/* The first two tabs are always here: what is owed, and where the cash
            that was already paid is sitting. The other two belong to the online
            payments module and appear only when it is switched on. */}
        <div className="flex gap-1 p-1 overflow-x-auto border rounded-xl border-slate-200 bg-slate-100 w-fit max-w-full">
          {([
            ["due", t("receivables.tabDue", "De încasat")] as const,
            ["coach-cash", t("receivables.tabCoachCash", "Numerar la antrenori")] as const,
            ...(features.paymentsOnline
              ? ([["received", t("receivables.tabReceived", "Încasări online")]] as const)
              : []),
            ...(features.paymentsDiscounts
              ? ([["waivers", t("receivables.tabWaivers", "Burse și scutiri")]] as const)
              : []),
          ]).map(([id, label]) => (
            <button
              key={id}
              onClick={() => goTab(id)}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap ${
                tab === id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* The flag is checked on the content as well as on the tab: a module
            switched off mid-session must not leave its panel on screen. */}
        {tab === "coach-cash" ? (
          <ClubCoachCash clubId={activeClubId} />
        ) : tab === "received" && features.paymentsOnline ? (
          activeClubId ? (
            <ClubOnlinePayments clubId={activeClubId} />
          ) : null
        ) : tab === "waivers" && features.paymentsDiscounts ? (
          activeClubId ? (
            <ClubWaivers clubId={activeClubId} />
          ) : null
        ) : (
          <>
        {/* Totals */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {tiles.map((tile) => {
            const body = (
              <>
                <p className="text-[11px] font-bold tracking-wide uppercase text-slate-400">{tile.label}</p>
                <p className={`mt-1 text-xl font-extrabold tabular-nums ${tile.tone}`}>{tile.value}</p>
              </>
            );
            return tile.goTo ? (
              <button
                key={tile.label}
                onClick={() => goTab(tile.goTo!)}
                className="p-4 text-left transition-colors bg-white border rounded-2xl border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30"
              >
                {body}
                <span className="block mt-1 text-[11px] font-semibold text-indigo-600">
                  {t("receivables.seeCoachCash", "vezi și preia →")}
                </span>
              </button>
            ) : (
              <div key={tile.label} className="p-4 bg-white border rounded-2xl border-slate-200">
                {body}
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <AdminCard>
          <div className="flex flex-wrap items-end gap-3 p-4">
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-500">{t("receivables.from", "De la")}</label>
              <DateInput value={from} onChange={setFrom} className={selectCls} />
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-500">{t("receivables.to", "Până la")}</label>
              <DateInput value={to} onChange={setTo} className={selectCls} />
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-500">{t("receivables.team", "Echipă")}</label>
              <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className={selectCls}>
                <option value="">{t("receivables.allTeams", "Toate echipele")}</option>
                {teams.map((tm) => (
                  <option key={tm.id} value={tm.id}>{tm.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-500">{t("receivables.coach", "Antrenor")}</label>
              <select value={coachId} onChange={(e) => setCoachId(e.target.value)} className={selectCls}>
                <option value="">{t("receivables.allCoaches", "Toți antrenorii")}</option>
                {coaches.map((c) => (
                  <option key={c.user.id} value={c.user.id}>{c.user.full_name || c.user.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-500">{t("receivables.status", "Status")}</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
                <option value="">{t("receivables.allStatuses", "Toate")}</option>
                <option value="pending">{t("receivables.pending", "De încasat")}</option>
                <option value="overdue">{t("receivables.overdue", "Restanță")}</option>
                <option value="paid">{t("receivables.paid", "Încasat")}</option>
              </select>
            </div>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-sm font-semibold transition-colors text-slate-500 hover:text-slate-800"
              >
                {t("common.clearFilters", "Resetează filtrele")}
              </button>
            )}
          </div>
        </AdminCard>

        {/* Rows */}
        <AdminCard>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 rounded-full border-indigo-600 animate-spin border-t-transparent" />
            </div>
          ) : error ? (
            <div className="py-16 text-center">
              <p className="text-slate-500">{t("error.loadFailed", "Nu s-au putut încărca datele")}</p>
              <button onClick={load} className="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-700">
                {t("common.retry", "Reîncearcă")}
              </button>
            </div>
          ) : !data || data.rows.length === 0 ? (
            <div className="py-16 text-center">
              <p className="font-medium text-slate-700">{t("receivables.emptyTitle", "Nimic de încasat")}</p>
              <p className="mt-1 text-sm text-slate-500">
                {hasFilters
                  ? t("receivables.emptyFiltered", "Niciun rezultat pentru filtrele alese.")
                  : t("receivables.emptyHint", "Alocă produse sau servicii sportivilor ca să apară aici.")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-3 font-semibold text-slate-600">{t("receivables.athlete", "Sportiv")}</th>
                    <th className="px-4 py-3 font-semibold text-slate-600">{t("receivables.product", "Produs / serviciu")}</th>
                    <th className="px-4 py-3 font-semibold text-right text-slate-600">{t("receivables.amount", "Sumă")}</th>
                    <th className="px-4 py-3 font-semibold text-slate-600">{t("receivables.due", "Scadent")}</th>
                    <th className="px-4 py-3 font-semibold text-slate-600">{t("receivables.status", "Status")}</th>
                    <th className="px-4 py-3 font-semibold text-slate-600">{t("receivables.whereMoney", "Unde sunt banii")}</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{row.athlete.full_name || row.athlete.email}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {row.teams.map((tm) => (
                            <span key={tm.id} className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-600">
                              {tm.name}
                            </span>
                          ))}
                        </div>
                        {row.coaches.length > 0 && (
                          <div className="mt-1 text-[11px] text-slate-400">
                            {t("receivables.coachLabel", "Antrenor")}: {row.coaches.map((c) => c.full_name).filter(Boolean).join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-700">{row.product.name}</div>
                        <div className="text-[11px] text-slate-400">{recurrenceLabels[row.product.recurrence_type]}</div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-right tabular-nums text-slate-800">
                        {money(row.amount_cents, row.currency)}
                      </td>
                      <td className="px-4 py-3 tabular-nums">
                        <span className={row.is_overdue ? "text-rose-600 font-medium" : "text-slate-600"}>
                          {row.next_due_date ? formatDate(row.next_due_date) : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3">{statusChip(row)}</td>
                      <td className="px-4 py-3">{cashLocation(row)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {features.paymentsInstalments && row.current_charge_id ? (
                          <button
                            onClick={() =>
                              setPlanFor({
                                chargeId: row.current_charge_id!,
                                athleteName: row.athlete.full_name || row.athlete.email,
                              })
                            }
                            className="px-3 py-1.5 text-xs font-semibold border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50"
                          >
                            {row.instalment_count > 0
                              ? t("receivables.planCount", "{{count}} rate", { count: row.instalment_count })
                              : t("receivables.plan", "Rate")}
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AdminCard>
          </>
        )}

        {planFor && (
          <ChargePlanDialog
            chargeId={planFor.chargeId}
            athleteName={planFor.athleteName}
            onClose={() => setPlanFor(null)}
            onChanged={load}
          />
        )}
      </div>
    </ClubAdminLayout>
  );
}
